import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import type { HouseholdBrief, MessageThread, PhotoAskPage, SessionState } from "@/shared/contracts";
import { queryKeys } from "@/shared/query/queryClient";
import { demoPhotoAskStatusService, livePhotoAskStatusService, type PhotoAskStatusService } from "./photoAskStatus.service";

const Context = createContext<PhotoAskStatusService | null>(null);
const live = livePhotoAskStatusService(apiClient), demo = demoPhotoAskStatusService();
export function PhotoAskStatusServiceProvider({ service, children }: { service: PhotoAskStatusService; children: ReactNode }) {
  return <Context.Provider value={service}>{children}</Context.Provider>;
}
type Scope = { userId: string | null; petId: string | undefined; householdId: string | null; homeId: string | null; identityKey: string; enabled: boolean };
type Snapshot = { life: object; page: PhotoAskPage | null; error: unknown };

/** Local snapshots never hydrate from a former identity's query/cache. No timer or cursor sweep. */
export function usePhotoAskStatus({ userId, petId, householdId, homeId, identityKey, enabled }: Scope) {
  const injected = useContext(Context), service = injected ?? (env.dataMode === "fixture" ? demo : live);
  const client = useQueryClient(), epoch = useRef(0);
  const life = useMemo(() => ({ alive: true, valid: true, epoch: ++epoch.current, serial: 0, controller: null as AbortController | null }), [userId, petId, identityKey, householdId, homeId, enabled, service, client]);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const refresh = useCallback(async () => {
    if (!enabled || !userId || !petId || !householdId || !homeId || !life.alive || !life.valid) return;
    // Explicit reads supersede only this GET; no stale data during or after an error.
    life.controller?.abort();
    const controller = new AbortController(), token = ++life.serial;
    life.controller = controller;
    setSnapshot({ life, page: null, error: null });
    try {
      const page = await service.latest(petId, controller.signal);
      if (!page || !Array.isArray(page.items) || page.items.length > 30 || (page.next_cursor !== null && typeof page.next_cursor !== "string")
        || page.items.some(row => !row || typeof row.request_id !== "string" || !row.request_id || typeof row.pet_id !== "string")) throw new Error("Invalid photo request page");
      if (life.alive && life.valid && life.serial === token && !controller.signal.aborted) setSnapshot({ life, page, error: null });
    } catch (error) {
      if (life.alive && life.valid && life.serial === token && !controller.signal.aborted) setSnapshot({ life, page: null, error });
    }
  }, [enabled, userId, petId, householdId, homeId, life, service]);
  useLayoutEffect(() => {
    life.alive = true; life.valid = true;
    const invalidate = () => {
      if (!life.valid) return;
      life.valid = false; life.serial++; life.controller?.abort();
      setSnapshot({ life, page: null, error: null });
    };
    const houseHash = JSON.stringify(queryKeys.households(userId ?? "-"));
    const messagesHash = JSON.stringify(queryKeys.messagesFor(userId ?? "-", petId ?? "-"));
    const unsubscribe = client.getQueryCache().subscribe(event => {
      if (!enabled || (event.type !== "updated" && event.type !== "removed")) return;
      const key = JSON.stringify(event.query.queryKey);
      if (key === houseHash) {
        const houses = event.query.state.data as HouseholdBrief[] | undefined;
        if (event.type === "removed" || event.query.state.status !== "success" || !Array.isArray(houses)
          || !houses.some(h => h.household_id === householdId && h.home_id === homeId && h.home_activated
            && h.pets.some(p => p.pet_id === petId && p.join_step === "moved_in"))) invalidate();
      } else if (key === JSON.stringify(queryKeys.session)) {
        const session = event.query.state.data as SessionState | undefined;
        if (event.type === "removed" || event.query.state.status !== "success" || !session?.authenticated || session.user?.user_id !== userId) invalidate();
      } else if (key === messagesHash) {
        const thread = event.query.state.data as MessageThread | undefined;
        if (event.type === "removed" || event.query.state.status === "error" || (thread && thread.pet_id !== petId)) invalidate();
      }
    });
    void refresh();
    return () => { unsubscribe(); life.alive = false; life.valid = false; life.serial++; life.controller?.abort(); };
  }, [client, life, enabled, userId, petId, householdId, homeId, refresh]);
  const current = enabled && life.valid && snapshot?.life === life ? snapshot : null;
  return { page: current?.page ?? null, error: current?.error ?? null, refresh,
    key: ["photo-ask-status", userId, petId, identityKey, life.epoch] as const };
}
